import React from 'react';
import PropTypes from 'prop-types';

import {
    RIQInfoPopover
} from './RIQPopover.jsx';

import RIQButton from './RIQButton.jsx';

import ExportDropdown from './ExportDropdown.jsx';

import {
    useIsExporting,
    useIsExportingPNG,
    useIsExportingPDF,
    useIsExportingPPT
} from '../hooks/useExport.js';

import {
    useGetFocusedInstanceIdValue,
    useGetFocusedInstanceIdState
} from '../hooks/widgetConfiguration.js';


const PanelFooter = function ({instanceId, footerContextView, showViewMore}) {

    const [focusedInstanceId, setFocusedInstanceId] = useGetFocusedInstanceIdState();

    const isExporting = useIsExporting();
    showViewMore = showViewMore && !focusedInstanceId;
    const hasFooter = footerContextView || (!isExporting && showViewMore);

    function onViewMoreClick() {
        setFocusedInstanceId(instanceId);
    }

    return (
        <>
            {hasFooter &&
                <div
                    className='tlw-flex tlw-flex-row tlw-items-center tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300 tlw-px-7.5 tlw-py-5'>

                    <div
                        className='tlw-grow tlw-text-sm'
                    >
                        {footerContextView}
                    </div>

                    {showViewMore && !isExporting &&
                        <RIQButton
                            className='tlw-px-6 tlw-py-3'
                            onClick={onViewMoreClick}>
                            View More
                        </RIQButton>
                    }
                </div>
            }
        </>
    );
};

PanelFooter.propTypes = {
    instanceId: PropTypes.string.isRequired,
    showViewMore: PropTypes.bool,
    additionalTitleBarControls: PropTypes.arrayOf(PropTypes.node)
};

const Panel = function ({instanceId, title, description, className, exportSubtitle, footerContextView, showViewMore, additionalTitleBarControls, children, isDescriptionHtml = true, isExportable = true}) {

    const isExporting = useIsExporting();
    const focusedInstanceId = useGetFocusedInstanceIdValue();
    const isFocusedExport = focusedInstanceId && isExporting;
    const isPNGExport = useIsExportingPNG();
    const isPDFExport = useIsExportingPDF();
    const isPPTExport = useIsExportingPPT();
    const isFocusedPNGExport = isPNGExport && isFocusedExport;
    const isFocusedPDFExport = isPDFExport && isFocusedExport;

    let showBorderBelowHeader = false;
    let containerClasses = className || '';
    if (isPNGExport || !isExporting) {
        containerClasses += ' tlw-rounded tlw-bg-white tlw-shadow-panel tlw-flex tlw-flex-col';
        showBorderBelowHeader = true;
    }

    if (!isFocusedExport) {
        containerClasses += ' tlw-mb-10';
    }

    const titleClasses = `tlw-m-0 tlw-text-gray-900 tlw-flex tlw-grow tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap ${isExporting ? 'tlw-leading-20' : 'tlw-leading-22 tlw-max-h-[43px]'} ${isPNGExport ? 'tlw-text-md' : 'tlw-text-base'}`;
    const panelHeaderClasses = `tlw-flex tlw-gap-[16px] tlw-py-5 ${showBorderBelowHeader ? 'tlw-border-0 tlw-border-b tlw-border-solid tlw-border-gray-300 tlw-px-7.5' : ''}`;

    return (
        <div className={containerClasses}>

            {!isPPTExport &&
                <div className={panelHeaderClasses}>
                    <div className='tlw-flex tlw-min-w-0 tlw-flex-1 tlw-flex-col'>
                        <h3 className={titleClasses}>
                            <span className='tlw-min-w-0 tlw-overflow-hidden tlw-text-ellipsis'>{title}</span>

                            {!isExporting && description &&
                                <span className='tlw-ml-2'>
                                    <RIQInfoPopover
                                        title={title}
                                        description={description}
                                        isDescriptionHtml={isDescriptionHtml}
                                    />
                                </span>
                            }
                        </h3>

                        {(isFocusedPDFExport || isFocusedPNGExport) &&
                            <h6 className='tlw-my-5 tlw-text-sm'>
                                {exportSubtitle}
                            </h6>
                        }
                    </div>

                    {!isExporting && !_.isEmpty(additionalTitleBarControls) &&
                        <>
                            {additionalTitleBarControls}
                        </>
                    }

                    {!isExporting && !focusedInstanceId && isExportable &&
                        <ExportDropdown
                            variant='small'
                            instanceId={instanceId}
                        />
                    }
                </div>
            }

            <div className='tlw-grow'>
                {children}
            </div>

            <PanelFooter
                instanceId={instanceId}
                footerContextView={footerContextView}
                showViewMore={showViewMore}
            />
        </div>
    );
};

Panel.propTypes = {
    instanceId: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.node,
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node
    ]).isRequired,
    exportSubtitle: PropTypes.string,
    footerContextView: PropTypes.node,
    showViewMore: PropTypes.bool,
    className: PropTypes.string
};

export default Panel;
